import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@styles/all-images/icons/search.svg";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import CustomLoader from "../loader/SpinnerLoader";

interface User {
  id: number;
  name: string;
  company: string;
  role: string;
  tier: string;
  users: any;
}

interface AutoCompleteSearchProps<T> {
  onSearch: (query: string) => Promise<T[]>;
  onSelect: (item: T | null) => void;
  isLoading?: boolean;
  placeholder?: string;
  renderItem?: (item: T, isSearchedRowLoading: boolean) => React.ReactNode;
  noResultsMessage?: string;
  loadingMessage?: string;
  minSearchLength?: number;
  isSearchedRowLoading?: boolean;
}

// Add this interface for the ref
export interface AutoCompleteSearchRef {
  handleClose: () => void;
  handleResetSearch: () => void;
}

const defaultRenderItem = (item: any, isSearchedRowLoading: boolean) => (
  <div className="tw-flex tw-items-center tw-p-3 hover:tw-bg-gray-50 tw-cursor-pointer">
    {/* Default Avatar */}
    <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-text-gray-600 tw-font-medium">
      {item.name?.charAt(0)}
    </div>

    {/* Default Details */}
    <div className="tw-ml-3 tw-flex-1">
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-font-medium">{item.name}</span>
      </div>
      <div className="tw-flex tw-items-center tw-text-sm tw-text-gray-500">
        <span>T{item?.users?.[0]?.user_type?.id}</span>
        {item.company_name && <span className="tw-mx-2">•</span>}
        <span>{item.company_name}</span>
        {item.role && <span className="tw-mx-2">•</span>}
        <span>{item.role}</span>
        {isSearchedRowLoading && (
          <div className="tw-ml-2">
            <CustomLoader loading={true} isWrapper={true} size="20" />
          </div>
        )}
      </div>
    </div>
  </div>
);

const AutoCompleteSearch = forwardRef(function AutoCompleteSearch<T>(
  {
    onSearch,
    onSelect,
    isLoading = false,
    placeholder = "Search...",
    renderItem = defaultRenderItem,
    noResultsMessage = "No results found",
    loadingMessage = "Loading...",
    minSearchLength = 2,
    isSearchedRowLoading = false,
  }: AutoCompleteSearchProps<T>,
  ref: React.Ref<AutoCompleteSearchRef>,
) {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm.length >= minSearchLength) {
        const searchResults = await onSearch(searchTerm);
        console.log("searchResults", searchResults);
        setResults(searchResults);
        setIsOpen(true);
      } else {
        setResults([]);
        setIsOpen(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, onSearch, minSearchLength]);

  const handleClose = () => {
    setIsOpen(false);
  };
  const handleResetSearch = () => {
    setSearchTerm("");
    setResults([]);
    onSelect(null);
    setSelectedItem(null);
  };

  useImperativeHandle(ref, () => ({
    handleClose,
    handleResetSearch,
  }));

  return (
    <div className="tw-relative">
      <TextField
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder={placeholder}
        variant="outlined"
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <img src={SearchIcon} alt="Search" className="tw-w-5 tw-h-5" />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <button
                onClick={() => {
                  handleClose();
                  handleResetSearch();
                }}
                className="tw-p-1 hover:tw-bg-gray-100 tw-rounded-full"
              >
                <img src={CloseIcon} alt="Clear" className="tw-w-4 tw-h-4" />
              </button>
            </InputAdornment>
          ),
          className: "tw-bg-white",
        }}
      />

      {isOpen && (
        <div className="tw-absolute tw-w-full tw-mt-1 tw-bg-white tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-200 tw-z-50">
          {isLoading ? (
            <div className="tw-p-3 tw-text-center tw-text-gray-500">
              {loadingMessage}
            </div>
          ) : results.length > 0 ? (
            results.map((item, index) => (
              <div
                key={index}
                onClick={() => {
                  onSelect(item);
                  setSelectedItem(item);
                }}
              >
                {renderItem(
                  item,
                  selectedItem?.id === item.id && isSearchedRowLoading,
                )}
              </div>
            ))
          ) : (
            <div className="tw-p-3 tw-text-center tw-text-gray-500">
              {noResultsMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default AutoCompleteSearch;
